/* Scroll video hero: the video plays as you scroll. */
(() => {
  /* only reduced motion gets the still hero; phones scroll the video too */
  const GATES = ['(prefers-reduced-motion: reduce)'];
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const smooth = (p, e0, e1) => { const t = clamp((p - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); };
  const rng = (seed) => { let s = seed >>> 0; return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296; };

  document.querySelectorAll('[data-sx]').forEach((sec, si) => {
    const stage = sec.querySelector('.sx-stage');
    const video = sec.querySelector('.sx-video');
    const posterLayer = sec.querySelector('.sx-poster');
    const ringStage = sec.querySelector('.sx-cue');
    const bar = sec.querySelector('.sx-progress');
    const src = sec.dataset.video;
    const bands = [...sec.querySelectorAll('.sx-band')].map(el => ({
      el, a: parseFloat(el.dataset.a), b: parseFloat(el.dataset.b),
      first: el.classList.contains('is-first'), last: el.classList.contains('is-last'),
      op: -1, k: -1, on: null
    }));

    /* split headings into words and letters, once */
    bands.forEach((band, bi) => {
      const h = band.el.querySelector('[data-split]');
      if (!h) return;
      const text = h.textContent.trim();
      const r = rng(1234 + bi * 97 + si);
      const focus = band.el.classList.contains('fx-focus');
      const build = () => {
        const vis = document.createElement('span');
        vis.setAttribute('aria-hidden', 'true');
        const words = text.split(/\s+/);
        let ci = 0;
        const total = text.replace(/\s/g, '').length;
        words.forEach((w, wi) => {
          const ws = document.createElement('span');
          ws.className = 'w';
          ws.style.setProperty('--th', (wi / Math.max(1, words.length) * 0.5).toFixed(3));
          [...w].forEach(ch => {
            const cs = document.createElement('span');
            cs.className = 'c';
            cs.textContent = ch;
            cs.style.setProperty('--th', (ci / total * 0.45 + r() * 0.08).toFixed(3));
            cs.style.setProperty('--jx', Math.round((r() - 0.5) * 120) + 'px');
            cs.style.setProperty('--jy', Math.round((r() - 0.5) * 90) + 'px');
            cs.style.setProperty('--jr', Math.round((r() - 0.5) * 40) + 'deg');
            ws.appendChild(cs);
            ci++;
          });
          vis.appendChild(ws);
          if (wi < words.length - 1) vis.appendChild(document.createTextNode(' '));
        });
        return vis;
      };
      const sr = document.createElement('span');
      sr.className = 'sr';
      sr.textContent = text;
      h.textContent = '';
      h.appendChild(sr);
      if (focus) {
        const soft = document.createElement('span'); soft.className = 'sx-soft'; soft.setAttribute('aria-hidden', 'true'); soft.textContent = text;
        const sharp = document.createElement('span'); sharp.className = 'sx-sharp'; sharp.setAttribute('aria-hidden', 'true'); sharp.textContent = text;
        h.append(soft, sharp);
      } else {
        h.appendChild(build());
      }
    });

    /* progress through the pinned section, 0..1 */
    const progress = () => {
      const r = sec.getBoundingClientRect();
      const range = sec.offsetHeight - innerHeight;
      return range > 0 ? clamp(-r.top / range, 0, 1) : 0;
    };

    /* captions: opacity + assembly, written only on change */
    const loadStart = performance.now();
    const drawBands = (p, now) => {
      bands.forEach(band => {
        const { a, b } = band;
        const f = Math.min(0.03, (b - a) / 3);
        let op = (band.first ? 1 : smooth(p, a, a + f)) * (band.last ? 1 : 1 - smooth(p, b - f, b));
        if (p < a && !band.first) op = 0;
        if (p > b && !band.last) op = 0;
        let k = clamp((p - a) / Math.min(0.06, (b - a) * 0.4), 0, 1);
        if (band.first) k = Math.max(k, clamp((now - loadStart) / 1400, 0, 1));
        if (Math.abs(op - band.op) > 0.004) { band.el.style.opacity = op.toFixed(3); band.op = op; }
        if (Math.abs(k - band.k) > 0.008) { band.el.style.setProperty('--k', k.toFixed(3)); band.k = k; }
        const on = op > 0.5;
        if (on !== band.on) { band.el.classList.toggle('is-on', on); band.on = on; }
      });
    };

    /* seek gating */
    let seekBusy = false, pending = null;
    const seek = (t) => {
      if (!video.duration) return;
      if (seekBusy) { pending = t; return; }
      seekBusy = true;
      video.currentTime = t;
    };
    video.addEventListener('seeked', () => {
      seekBusy = false;
      if (pending !== null) { const t = pending; pending = null; seek(t); }
    });
    video.addEventListener('error', () => { seekBusy = false; pending = null; });

    /* the rAF loop, which rests when done */
    let target = 0, shown = 0, raf = null, last = 0, lastScrolled = null, lastBar = -1, onScreen = true;
    const tick = (now) => {
      const dt = Math.min(100, now - (last || now));
      last = now;
      shown += (target - shown) * (1 - Math.pow(1 - 0.14, dt / 16.667));
      const loading = now - loadStart < 1500;
      if (Math.abs(target - shown) < 0.0005 && !loading) { shown = target; raf = null; last = 0; }
      else raf = requestAnimationFrame(tick);
      if (video.duration) seek(shown * (video.duration - 0.05));
      drawBands(shown, now);
      if (Math.abs(shown - lastBar) > 0.002) { bar.style.setProperty('--p', shown.toFixed(3)); lastBar = shown; }
      const sc = shown > 0.02;
      if (sc !== lastScrolled) { sec.classList.toggle('is-scrolled', sc); lastScrolled = sc; }
    };
    const onScroll = () => {
      target = progress();
      if (raf === null && onScreen) raf = requestAnimationFrame(tick);
    };
    new IntersectionObserver(([en]) => { onScreen = en.isIntersecting; if (onScreen) onScroll(); }).observe(sec);

    /* load the poster first, then the video as a blob with an honest ring */
    let inited = false;
    const failVideo = () => { sec.classList.add('video-failed'); ringStage.style.setProperty('--ld', 0); };
    const loadVideo = async () => {
      if (!src) return failVideo();
      const ctrl = new AbortController();
      let dog = setTimeout(() => ctrl.abort(), 20000);
      const res = await fetch(src, { signal: ctrl.signal, priority: 'low' });
      if (!res.ok || !res.body) throw new Error('video');
      const total = Number(res.headers.get('Content-Length')) || Number(sec.dataset.bytes) || 8e6;
      const reader = res.body.getReader();
      const chunks = [];
      let got = 0, lastRing = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        clearTimeout(dog); dog = setTimeout(() => ctrl.abort(), 20000);
        chunks.push(value); got += value.length;
        const now = performance.now();
        if (now - lastRing > 100) { lastRing = now; ringStage.style.setProperty('--ld', Math.round(126 * (1 - Math.min(1, got / total)))); }
      }
      clearTimeout(dog);
      ringStage.style.setProperty('--ld', 0);
      video.src = URL.createObjectURL(new Blob(chunks, { type: 'video/mp4' }));
      video.load();
      video.addEventListener('canplay', () => {
        /* phones (iOS especially) only paint seeked frames after one play() */
        const p = video.play();
        const settle = () => { video.pause(); seek(progress() * (video.duration - 0.05)); sec.classList.add('video-ready'); };
        if (p && p.then) p.then(settle).catch(settle); else settle();
      }, { once: true });
    };
    const initOnce = () => {
      if (inited) return; inited = true;
      const start = () => loadVideo().catch(failVideo);
      if (sec.dataset.poster) {
        posterLayer.style.backgroundImage = `url('${sec.dataset.poster}')`;
        const img = new Image();
        let fired = false;
        const go = () => { if (!fired) { fired = true; start(); } };
        img.onload = go; img.onerror = go; img.src = sec.dataset.poster;
        setTimeout(go, 4000);
      } else start();
    };

    /* the five gates, decided live */
    let on = false;
    const enable = () => {
      if (on) return; on = true;
      initOnce();
      addEventListener('scroll', onScroll, { passive: true });
      bands.forEach(b => { b.op = -1; b.k = -1; b.on = null; });
      onScroll();
    };
    const disable = () => {
      if (!on) return; on = false;
      removeEventListener('scroll', onScroll);
      if (raf !== null) { cancelAnimationFrame(raf); raf = null; }
      bands.forEach(b => { b.el.style.opacity = ''; b.el.style.removeProperty('--k'); });
    };
    const mqls = GATES.map(q => matchMedia(q));
    const apply = () => (mqls.some(m => m.matches) ? disable() : enable());
    mqls.forEach(m => m.addEventListener('change', apply));
    apply();
  });
})();
